import {useState, useEffect, useCallback, useMemo} from 'react';
import fetchLayerTypes from '../utils/gaia/fetchLayerTypes';
import fetchUserLayers from '../utils/gaia/fetchUserLayers';
import fetchDefaultUserLayers from '../utils/gaia/fetchDefaultUserLayers';
import cleanUserLayers from '../utils/gaia/cleanUserLayers';
import saveUserLayers from '../utils/gaia/saveUserLayers';
import {pickBy} from 'lodash';
import useIsSaved from './useIsSaved';
import trackAnalytics from '../utils/gaia/trackAnalytics';
import fetchUser from '../utils/gaia/fetchUser';
import trackLayerAnalytics from '../utils/gaia/trackLayerAnalytics';

interface AddAction {type: 'add'; layer: LayerType}
interface UpdateAction {type: 'update'; layers: UserLayer[]}
interface RemoveAction {type: 'remove'; layer: LayerType}
type Action = (
    AddAction |
    UpdateAction |
    RemoveAction
);

interface State {
    defaultUserLayers: UserLayer[] | undefined;
    layerTypes: {[key: string]: LayerType} | undefined;
    isUserLayersEphemeral: boolean;
    userLayers: UserLayer[];
    user: User | undefined;
}

export type UserLayerDispatcher = (action: Action) => void;

type UseLayersOutput = [
    {
        layers: Layer[] | undefined;
        userLayers: UserLayer[] | undefined;
        layerTypes: Record<string, LayerType> | undefined;
    },
    UserLayerDispatcher,
    boolean
]

export default function useLayers(
    propsLayers: Layer[] | undefined,
    urlPropsLayers: string[] | undefined,
    {isLayered}: {isLayered: boolean | undefined}
): UseLayersOutput {

    const [state, setState] = useState<State>({
        user: undefined,
        defaultUserLayers: undefined,
        layerTypes: undefined,
        isUserLayersEphemeral: false,
        userLayers: [],
    });
    const [savedUserLayers, setSavedUserLayers] = useState<UserLayer[] | undefined>();
    const [erroredUserLayers, setErroredUserLayers] = useState<UserLayer[] | undefined>();
    const [isSaved, addSavePromise] = useIsSaved();

    useEffect(() => {
        (async () => {
            const [
                user,
                initialUserLayers,
                defaultUserLayers,
                layerTypes,
            ] = await Promise.all([
                fetchUser(),
                fetchUserLayers(urlPropsLayers),
                fetchDefaultUserLayers(),
                fetchLayerTypes(),
            ]);

            setSavedUserLayers(initialUserLayers.savedUserLayers);
            setState({
                ...state,
                user,
                defaultUserLayers,
                layerTypes,
                isUserLayersEphemeral: initialUserLayers.isUserLayersEphemeral,
                userLayers: cleanUserLayers({
                    user,
                    userLayers: initialUserLayers.userLayers,
                    isLayered,
                    defaultUserLayers,
                    layerTypes,
                })
            });
            trackLayerAnalytics(
                'Set Map Source',
                initialUserLayers.userLayers,
                {category: 'map2'}
            );

        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = useCallback(async (
        nextUserLayers: UserLayer[],
        layerTypes?: State['layerTypes']
    ) => {

        setState({
            ...state,
            userLayers: nextUserLayers,
            layerTypes: layerTypes || state.layerTypes,
        });
        try {
            setSavedUserLayers(await saveUserLayers(
                nextUserLayers,
                savedUserLayers,
                erroredUserLayers,
                addSavePromise,
            ));
        } catch (e) {
            setErroredUserLayers(nextUserLayers);
            throw e;
        }
        return;
    }, [addSavePromise, erroredUserLayers, savedUserLayers, state]);

    const add = useCallback((action: AddAction) => {
        if (!state.layerTypes || !state.defaultUserLayers) return;
        trackAnalytics('Map Source - Add', {
            category: 'map2',
            layerName: action.layer.uniqueTilecacheKey
        });
        const nextUserLayers = cleanUserLayers({
            user: state.user,
            userLayers: [
                ...state.userLayers,
                {layer: {opacity: 1, type: action.layer.uniqueTilecacheKey}, isVisible: true}
            ],
            isLayered,
            layerTypes: state.layerTypes,
            defaultUserLayers: state.defaultUserLayers,
        });
        return save(nextUserLayers);
    }, [isLayered, save, state.defaultUserLayers, state.layerTypes, state.user, state.userLayers]);

    const update = useCallback((action: UpdateAction) => {
        if (!state.layerTypes || !state.defaultUserLayers) return;
        const nextUserLayers = cleanUserLayers({
            user: state.user,
            userLayers: action.layers,
            isLayered,
            layerTypes: state.layerTypes,
            defaultUserLayers: state.defaultUserLayers,
        });
        return save(nextUserLayers);
    }, [isLayered, save, state.defaultUserLayers, state.layerTypes, state.user]);

    const remove = useCallback((action: RemoveAction) => {
        if (!state.layerTypes || !state.defaultUserLayers) return;
        const nextUserLayers = cleanUserLayers({
            user: state.user,
            userLayers: state.userLayers.filter((layer: UserLayer) =>
                action.layer.uniqueTilecacheKey !== layer.layer.type
            ),
            isLayered,
            layerTypes: state.layerTypes,
            defaultUserLayers: state.defaultUserLayers
        });

        let layerTypes = state.layerTypes;
        if (action.layer.categories[0] === 'custom') {
            const message = (
                `This will delete "${action.layer.title}" from your account. ` +
                'Do you wish to continue?'
            );

            if (!confirm(message)) {
                return action.layer;
            }

            layerTypes = pickBy(layerTypes, otherLayerType => otherLayerType !== action.layer);
        }
        trackAnalytics('Map Source - Remove', {
            category: 'map2',
            layerName: action.layer.uniqueTilecacheKey
        });
        return save(nextUserLayers, layerTypes);
    }, [isLayered, save, state.defaultUserLayers, state.layerTypes, state.user, state.userLayers]);

    const dispatcher = useCallback((action: Action) => {
        if (!isSaved) return;

        if (action.type === 'add') return add(action);
        else if (action.type === 'update') return update(action);
        else if (action.type === 'remove') return remove(action);
        throw new Error('Unknown layer action');
    }, [add, isSaved, remove, update]);

    const layers = useMemo(() => propsLayers ?
        propsLayers :
        state.userLayers ?
            state.userLayers
                .filter((userLayer: UserLayer) => userLayer.isVisible)
                .map((userLayer: UserLayer) => userLayer.layer) :
            undefined
    , [propsLayers, state.userLayers]);

    return [{
        userLayers: state.userLayers,
        layerTypes: state.layerTypes,
        layers
    }, dispatcher, isSaved];
}
